/**
 * Subdomain detection utilities for public websites
 * Patterns:
 *   - Production: *.website.fusionads.ai
 *   - Staging: *.stage.website.fusionads.ai
 */

// Base domains for website subdomains
const WEBSITE_BASE_DOMAIN_PROD = 'website.fusionads.ai';
const WEBSITE_BASE_DOMAIN_STAGE = 'stage.website.fusionads.ai';

/**
 * Check if hostname contains the staging domain pattern
 * Uses includes instead of endsWith to handle edge cases
 */
const isStageHostname = (hostname: string): boolean => {
	// Check if 'stage.website.fusionads.ai' appears in the hostname
	return hostname.includes('stage.website.fusionads.ai');
};

/**
 * Get the current website base domain based on environment
 * Returns the appropriate base domain (production or staging)
 */
export const getWebsiteBaseDomain = (): string => {
	const hostname = window.location.hostname.toLowerCase();
	
	// Check staging first (more specific pattern)
	// Use includes to ensure we catch all staging patterns
	if (isStageHostname(hostname)) {
		return WEBSITE_BASE_DOMAIN_STAGE;
	}
	
	// Check production
	if (hostname.includes(WEBSITE_BASE_DOMAIN_PROD)) {
		return WEBSITE_BASE_DOMAIN_PROD;
	}
	
	// Default to production
	return WEBSITE_BASE_DOMAIN_PROD;
};

/**
 * Check if current hostname is a website subdomain
 * Examples:
 *   - my-campaign.website.fusionads.ai → true (production)
 *   - my-campaign.stage.website.fusionads.ai → true (staging)
 *   - app.fusionads.ai → false
 *   - localhost:3000 → false
 */
export const isWebsiteSubdomain = (): boolean => {
	const hostname = window.location.hostname.toLowerCase();
	
	// Check staging domain (more specific pattern first)
	if (isStageHostname(hostname)) {
		// Extract what comes before stage.website.fusionads.ai
		const parts = hostname.split('.stage.website.fusionads.ai');
		const subdomain = parts[0];
		// Valid if there's a subdomain and it doesn't contain dots
		return subdomain.length > 0 && !subdomain.includes('.');
	}
	
	// Check production domain
	if (hostname.includes(WEBSITE_BASE_DOMAIN_PROD)) {
		// Extract what comes before website.fusionads.ai
		const parts = hostname.split('.website.fusionads.ai');
		const subdomain = parts[0];
		// Valid if there's a subdomain and it doesn't contain dots
		return subdomain.length > 0 && !subdomain.includes('.');
	}
	
	return false;
};

/**
 * Extract subdomain from current hostname
 * Examples:
 *   - my-campaign.website.fusionads.ai → "my-campaign"
 *   - my-campaign.stage.website.fusionads.ai → "my-campaign"
 */
export const getWebsiteSubdomain = (): string | null => {
	const hostname = window.location.hostname.toLowerCase();
	
	// Check staging domain first (more specific pattern)
	if (isStageHostname(hostname)) {
		// Split by the staging base domain
		const parts = hostname.split('.stage.website.fusionads.ai');
		const subdomain = parts[0];
		if (subdomain && subdomain.length > 0 && !subdomain.includes('.')) {
			return subdomain;
		}
		return null;
	}
	
	// Check production domain
	if (hostname.includes(WEBSITE_BASE_DOMAIN_PROD)) {
		// Split by the production base domain
		const parts = hostname.split('.website.fusionads.ai');
		const subdomain = parts[0];
		if (subdomain && subdomain.length > 0 && !subdomain.includes('.')) {
			return subdomain;
		}
		return null;
	}
	
	return null;
};

/**
 * Check if we're on the staging website domain
 */
export const isStageWebsiteDomain = (): boolean => {
	const hostname = window.location.hostname.toLowerCase();
	return hostname.endsWith(`.${WEBSITE_BASE_DOMAIN_STAGE}`);
};

/**
 * Get the full domain for API calls
 * Returns the domain WITH the environment-specific suffix
 * Examples:
 *   - my-campaign.website.fusionads.ai → "my-campaign.website.fusionads.ai" (production)
 *   - my-campaign.stage.website.fusionads.ai → "my-campaign.stage.website.fusionads.ai" (staging)
 * 
 * Staging and production use different domain formats stored separately
 */
export const getFullDomainForLookup = (): string | null => {
	const subdomain = getWebsiteSubdomain();
	if (!subdomain) return null;
	
	// Return with the correct environment suffix
	const baseDomain = getWebsiteBaseDomain();
	return `${subdomain}.${baseDomain}`;
};

/**
 * Check if we should show the public website instead of the dashboard
 * This should be called early in the app lifecycle
 */
export const shouldShowPublicWebsite = (): boolean => {
	return isWebsiteSubdomain() && getWebsiteSubdomain() !== null;
};

// Legacy export for backwards compatibility
export const WEBSITE_BASE_DOMAIN = WEBSITE_BASE_DOMAIN_PROD;

export default {
	isWebsiteSubdomain,
	getWebsiteSubdomain,
	getFullDomainForLookup,
	shouldShowPublicWebsite,
	getWebsiteBaseDomain,
	isStageWebsiteDomain,
	WEBSITE_BASE_DOMAIN,
	WEBSITE_BASE_DOMAIN_PROD,
	WEBSITE_BASE_DOMAIN_STAGE,
};
